import {
  parseFontSize as parseStoredFontSize,
  parseHourRange,
  parseMaxWidth as parseStoredMaxWidth,
  type FontSize,
  type HourRange,
} from "@ii-gp-widget/shared/browser";
export type { FontSize, HourRange };

// Parsing of the embedder-facing `<script data-*>` contract. Kept out of index.ts so it can
// be unit-tested without a DOM (index.ts boots itself on import).

/**
 * `data-max-width` caps how wide the widget grows inside its container; without it the
 * widget stops at the historical 420px. `none` lets it fill the container completely.
 *
 * The value is embedder-authored text that ends up in an inline custom property, so it is
 * validated down to a single length rather than passed through. A bare number is read as
 * px, which is what someone writing `data-max-width="640"` means.
 */
export type FontMode = "inter" | "inherit";

/**
 * `data-font`: `inter` (default) loads the widget's own self-hosted Inter; `inherit` takes
 * the host page's font instead and skips the font download entirely, so the widget reads as
 * part of the site it sits in.
 */
export function parseFont(raw: string | undefined): FontMode {
  const value = raw?.trim().toLowerCase();
  if (!value || value === "inter") return "inter";
  if (value === "inherit") return "inherit";
  console.error(`[ii-gp-widget] ignoring invalid data-font value: ${raw} (expected "inter" or "inherit")`);
  return "inter";
}

/**
 * `data-aipt`: `on` (default) shows the AI workout-plan section, `off` leaves it out for
 * embedders who only want the occupancy data.
 */
export function parseAipt(raw: string | undefined): boolean {
  const value = raw?.trim().toLowerCase();
  if (!value || value === "on") return true;
  if (value === "off") return false;
  console.error(`[ii-gp-widget] ignoring invalid data-aipt value: ${raw} (expected "on" or "off")`);
  return true;
}

/**
 * `data-privacy-url`: the privacy policy the footer links to. There is no default — the
 * consent the plan-email form collects is the gym's, so the gym names its own policy — and
 * only http(s) is accepted, since the value lands in an href. `base` (the host page's URL)
 * is what a relative value resolves against; it is a parameter rather than a read of
 * `document` so this module stays testable without a DOM.
 */
export function parsePrivacyUrl(raw: string | undefined, base?: string): string | null {
  const value = raw?.trim();
  if (!value) return null;
  try {
    const url = new URL(value, base);
    if (url.protocol === "http:" || url.protocol === "https:") return url.toString();
  } catch {
    // falls through to the error below
  }
  console.error(`[ii-gp-widget] ignoring invalid data-privacy-url value: ${raw} (expected an http(s) URL)`);
  return null;
}

/**
 * `data-hours="6-24"` overrides the premise's stored opening hours for this embed, limiting
 * the curves to the hours the gym is open so the plot is not half flat line. Same format as
 * the stored setting, and the same parser, so the two can't drift apart.
 */
export function parseHours(raw: string | undefined): HourRange | null {
  const value = raw?.trim();
  if (!value) return null;
  const range = parseHourRange(value);
  if (range) return range;
  // "0-24" parses to null too, but that is the default rather than a mistake worth logging.
  if (value.replace(/\s/g, "") !== "0-24") {
    console.error(`[ii-gp-widget] ignoring invalid data-hours value: ${raw} (expected "START-END" on a 24-hour clock, at least 2 hours apart, e.g. "6-24")`);
  }
  return null;
}

export function parseMaxWidth(raw: string | undefined): string | null {
  const value = raw?.trim();
  if (!value) return null;
  const parsed = parseStoredMaxWidth(value);
  if (parsed) return parsed;
  console.error(`[ii-gp-widget] ignoring invalid data-max-width value: ${raw}`);
  return null;
}

/**
 * `data-font-size="small" | "medium" | "large"` overrides the premise's stored text size (the
 * widget admin's setting) for this embed. Same parser as the stored setting.
 */
export function parseFontSize(raw: string | undefined): FontSize | null {
  const value = raw?.trim();
  if (!value) return null;
  const parsed = parseStoredFontSize(value);
  if (parsed) return parsed;
  console.error(`[ii-gp-widget] ignoring invalid data-font-size value: ${raw} (expected "small", "medium" or "large")`);
  return null;
}

export type DisplayMode = "full" | "onlytotal";

/**
 * `data-type="onlytotal"` renders a compact version — just the live total and a "see more"
 * link — for pages that cannot fit the regular embed. "See more" opens the full widget in a
 * modal, mounted from the same data already on the page rather than a second request.
 * Default `data-type="full"` is today's widget, unchanged.
 */
export function parseDisplayMode(raw: string | undefined): DisplayMode {
  const value = raw?.trim().toLowerCase();
  if (!value || value === "full") return "full";
  if (value === "onlytotal") return "onlytotal";
  console.error(`[ii-gp-widget] ignoring invalid data-type value: ${raw} (expected "full" or "onlytotal")`);
  return "full";
}
